/**
 * FamilyManager — family group management for Mini App
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import type {
  FamilyGroup,
  FamilyRole,
  SavedChartSummary,
} from '../../types';
import { FAMILY_ROLE_LABELS } from '../../types';
import { markdownToHtml } from '../../utils/markdown';
import styles from './FamilyManager.module.css';

type SubView = 'dashboard' | 'create' | 'add_chart' | 'add_role' | 'forecast';

interface Props {
  onBack: () => void;
  onCreateChart: () => void;
}

export default function FamilyManager({ onBack, onCreateChart }: Props) {
  const [family, setFamily] = useState<FamilyGroup | null>(null);
  const [subView, setSubView] = useState<SubView>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create flow
  const [familyName, setFamilyName] = useState('');

  // Add member flow
  const [charts, setCharts] = useState<SavedChartSummary[]>([]);
  const [selectedChartId, setSelectedChartId] = useState<string | null>(null);
  const [selectedChartName, setSelectedChartName] = useState<string>('');

  // Forecast
  const [forecastText, setForecastText] = useState<string | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);

  const loadFamily = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getFamilyGroup();
      if (response.success) {
        setFamily(response.family);
        if (!response.family) {
          setSubView('create');
        } else {
          setSubView('dashboard');
        }
      } else {
        setError(response.error || 'Failed to load family');
      }
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFamily();
  }, [loadFamily]);

  const handleCreateFamily = async () => {
    if (!familyName.trim()) return;
    setLoading(true);
    try {
      const response = await api.createFamily(familyName.trim());
      if (response.success && response.family) {
        setFamily(response.family);
        setSubView('dashboard');
        setFamilyName('');
      } else {
        setError(response.error || 'Failed to create family');
      }
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFamily = async () => {
    if (!family) return;
    setLoading(true);
    try {
      const response = await api.deleteFamily(family.id);
      if (response.success) {
        setFamily(null);
        setSubView('create');
      }
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAddMember = async () => {
    if (!family) return;
    setLoading(true);
    try {
      const chartsResponse = await api.getUserCharts();
      if (chartsResponse.success) {
        const existingChartIds = new Set(family.members.map((m) => m.chartId));
        const available = chartsResponse.charts.filter((c) => !existingChartIds.has(c.id));
        setCharts(available);
        setSubView('add_chart');
      }
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChart = (chartId: string, chartName: string) => {
    setSelectedChartId(chartId);
    setSelectedChartName(chartName || 'Без названия');
    setSubView('add_role');
  };

  const handleSelectRole = async (role: FamilyRole) => {
    if (!family || !selectedChartId) return;
    setLoading(true);
    try {
      const response = await api.addFamilyMember(
        family.id,
        selectedChartId,
        role,
        selectedChartName
      );
      if (response.success && response.family) {
        setFamily(response.family);
        setSubView('dashboard');
      } else {
        setError(response.error || 'Failed to add member');
      }
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
      setSelectedChartId(null);
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!family) return;
    try {
      const response = await api.removeFamilyMember(family.id, memberId);
      if (response.success && response.family) {
        setFamily(response.family);
      }
    } catch {
      setError('Connection error');
    }
  };

  const handleToggleForecast = async () => {
    if (!family) return;
    try {
      const response = await api.toggleFamilyForecast(family.id);
      if (response.success) {
        setFamily({ ...family, forecastEnabled: response.forecastEnabled });
      } else {
        setError(response.error || 'Failed to toggle forecast');
      }
    } catch {
      setError('Connection error');
    }
  };

  const handleGetForecast = async () => {
    if (!family) return;
    setForecastLoading(true);
    setForecastText(null);
    setSubView('forecast');
    try {
      const response = await api.getFamilyForecast(family.id);
      if (response.success && response.forecast) {
        setForecastText(response.forecast);
      } else {
        setError(response.error || 'Failed to generate forecast');
        setSubView('dashboard');
      }
    } catch {
      setError('Connection error');
      setSubView('dashboard');
    } finally {
      setForecastLoading(false);
    }
  };

  if (loading && !family && subView === 'dashboard') {
    return <div className={styles.loading}>Загрузка...</div>;
  }

  return (
    <div className={styles.container}>
      {error && (
        <div style={{ color: 'var(--tg-theme-destructive-text-color, red)', marginBottom: 12 }} onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {/* Create family */}
      {subView === 'create' && (
        <div>
          <div className={styles.header}>
            <h2>Создать семью</h2>
          </div>
          <p>Объедините натальные карты близких и получайте ежедневные семейные прогнозы.</p>
          <div className={styles.createForm}>
            <input
              className={styles.input}
              type="text"
              placeholder="Название семьи"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              maxLength={255}
            />
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCreateFamily} disabled={loading}>
              {loading ? 'Создаю...' : 'Создать'}
            </button>
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onBack}>
              Назад
            </button>
          </div>
        </div>
      )}

      {/* Dashboard */}
      {subView === 'dashboard' && family && (
        <div>
          <div className={styles.header}>
            <h2>👨‍👩‍👧‍👦 {family.name}</h2>
          </div>

          <div className={styles.memberList}>
            {family.members.map((member) => (
              <div key={member.id} className={styles.memberCard}>
                <div className={styles.memberInfo}>
                  <span className={styles.memberRole}>{FAMILY_ROLE_LABELS[member.role as FamilyRole] || member.role}</span>
                  <span className={styles.memberName}>{member.displayName || '—'}</span>
                  {member.sunSignRu && <span className={styles.memberSign}>☀️ {member.sunSignRu}</span>}
                </div>
                <button className={styles.removeBtn} onClick={() => handleRemoveMember(member.id)}>
                  Убрать
                </button>
              </div>
            ))}
          </div>

          {family.members.length < 2 && (
            <div className={styles.hint}>Добавьте минимум 2 участника для получения прогнозов</div>
          )}

          <div className={styles.forecastStatus}>
            Ежедневный прогноз: {family.forecastEnabled ? 'Включён' : 'Отключён'}
          </div>

          <div className={styles.actions}>
            {family.members.length < 8 && (
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleStartAddMember}>
                + Добавить участника
              </button>
            )}
            {family.members.length >= 2 && (
              <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={handleGetForecast}>
                Прогноз на сегодня
              </button>
            )}
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={handleToggleForecast}>
              {family.forecastEnabled ? 'Выключить' : 'Включить'} ежедневный прогноз
            </button>
            <button className={`${styles.btn} ${styles.btnDanger}`} onClick={handleDeleteFamily}>
              Удалить семью
            </button>
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onBack}>
              Назад
            </button>
          </div>
        </div>
      )}

      {/* Select chart */}
      {subView === 'add_chart' && (
        <div>
          <div className={styles.header}>
            <h2>Выберите карту</h2>
          </div>
          {charts.length === 0 ? (
            <div className={styles.hint}>
              Нет доступных карт. Создайте новую карту, затем добавьте в семью.
            </div>
          ) : (
            <div className={styles.chartList}>
              {charts.map((chart) => (
                <button
                  key={chart.id}
                  className={styles.chartBtn}
                  onClick={() => handleSelectChart(chart.id, chart.name || '')}
                >
                  {chart.name || 'Без названия'} — ☀️ {chart.sunSignRu}
                </button>
              ))}
            </div>
          )}
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={onCreateChart} style={{ marginTop: 12 }}>
            ➕ Создать новую карту
          </button>
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setSubView('dashboard')} style={{ marginTop: 12 }}>
            Отмена
          </button>
        </div>
      )}

      {/* Select role */}
      {subView === 'add_role' && (
        <div>
          <div className={styles.header}>
            <h2>Выберите роль</h2>
          </div>
          <p>Кем является {selectedChartName} в семье?</p>
          <div className={styles.roleGrid}>
            {(Object.entries(FAMILY_ROLE_LABELS) as [FamilyRole, string][]).map(([role, label]) => (
              <button key={role} className={styles.roleBtn} onClick={() => handleSelectRole(role)} disabled={loading}>
                {label}
              </button>
            ))}
          </div>
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setSubView('add_chart')} style={{ marginTop: 12 }}>
            Назад
          </button>
        </div>
      )}

      {/* Forecast view */}
      {subView === 'forecast' && (
        <div>
          <div className={styles.header}>
            <h2>Семейный прогноз</h2>
          </div>
          {forecastLoading ? (
            <div className={styles.loading}>Генерирую прогноз...</div>
          ) : forecastText ? (
            <div className={styles.forecastText} dangerouslySetInnerHTML={{ __html: markdownToHtml(forecastText) }} />
          ) : null}
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setSubView('dashboard')} style={{ marginTop: 12 }}>
            Назад
          </button>
        </div>
      )}
    </div>
  );
}
